import { Dispatch, SetStateAction, useState } from 'react'
import styles from '../../assets/scss/components/products/product-accordion.module.scss'
import { AcfMarkdown } from '../../lib/acf'
import { AnimatePresence, motion } from 'framer-motion'
import { useI18n } from '../i18n.client'
import { ProductMetafields } from '../../lib/types'
import { classNames } from '@shopify/css-utilities'
import { useTracking } from '../../lib/hooks/tracking.client'

export const ProductAccordion: React.FC<{
  descriptions: AcfMarkdown[]
  product: ProductMetafields
  titles: string[]
  variant?: string
}> = ({ descriptions, product, titles, variant }) => {
  const [i18n] = useI18n()
  const [activeIndex, setActiveIndex] = useState<number>()

  return (
    <div className={classNames(styles.accordion, variant && styles[variant])}>
      <AccordionSegment
        activeIndex={activeIndex}
        descriptionHtml={product.details?.value && `${product.details?.value}\n\n`}
        index={0}
        setActiveIndex={setActiveIndex}
        title={i18n.translate('details')}
      />
      {titles.map((title, index) => (
        <AccordionSegment
          activeIndex={activeIndex}
          descriptionHtml={descriptions[index].html}
          index={index + (product.details?.value ? 1 : 0)}
          key={index}
          setActiveIndex={setActiveIndex}
          title={title}
        />
      ))}
    </div>
  )
}

const AccordionSegment: React.FC<{
  activeIndex?: number
  descriptionHtml?: string
  index: number
  setActiveIndex: Dispatch<SetStateAction<number | undefined>>
  title: string
}> = ({ activeIndex, descriptionHtml, index, setActiveIndex, title }) => {
  const { trackEvent } = useTracking()

  if (!descriptionHtml) {
    return null
  }

  const handleClick = (index: number) => {
    if (index === activeIndex) {
      setActiveIndex(undefined)
    } else {
      trackEvent('information_toggle')
      setActiveIndex(index)
    }
  }

  return (
    <div className={styles.segment}>
      <div className={styles.title} onClick={() => handleClick(index)}>
        {title}
        <div className={styles.icon}>{index === activeIndex ? '–' : '+'}</div>
      </div>
      <AnimatePresence>
        {index === activeIndex && (
          <motion.div
            animate={{ opacity: 1, height: 'auto' }}
            className={styles.description}
            dangerouslySetInnerHTML={{ __html: descriptionHtml }}
            exit={{ opacity: 0, height: 0 }}
            initial={{ opacity: 0, height: 0 }}
            transition={{ ease: [0, 0, 0, 1] }}
          />
        )}
      </AnimatePresence>
    </div>
  )
}
